import { Cat, KINDS, kindIndex } from '../../../gameplay/registry';
import { FX_SHIFT } from '../fixed';
import type { World } from '../world';

const circleCache = new Map<number, Int16Array>();

/** Offsets (dx,dy pairs) of tiles within radius r. */
function circle(r: number): Int16Array {
  let c = circleCache.get(r);
  if (!c) {
    const pts: number[] = [];
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) if (dx * dx + dy * dy <= r * r + r) pts.push(dx, dy);
    c = Int16Array.from(pts);
    circleCache.set(r, c);
  }
  return c;
}

export const VISION_INTERVAL = 4;

/**
 * Recompute fog of war: per-player visibility with team-shared vision.
 * 0 = unexplored, 1 = explored (not visible), 2 = currently visible.
 */
export function updateVision(w: World, force = false): void {
  if (!force && w.tick % VISION_INTERVAL !== 0) return;
  const size = w.size;
  const np = w.players.length;
  while (w.visibility.length < np) w.visibility.push(new Uint8Array(size * size));
  // Demote visible -> explored.
  for (let p = 0; p < np; p++) {
    const v = w.visibility[p];
    for (let i = 0; i < v.length; i++) if (v[i] === 2) v[i] = 1;
  }
  // Teams share vision: stamp into every allied player's map.
  const allies: number[][] = w.players.map((p) => w.players.filter((q) => (q.team === p.team && p.team >= 0) || q.id === p.id).map((q) => q.id));
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    if (!w.alive[s] || w.cat[s] === Cat.Resource || w.cat[s] === Cat.Held) continue;
    // A building's sight (like its other powers) only starts once construction is complete.
    if (w.cat[s] === Cat.Building && !w.built[s]) continue;
    const o = w.owner[s];
    if (o < 0) continue;
    const r = w.players[o].stats[w.kind[s]].los;
    const pts = circle(r);
    const cx = w.x[s] >> FX_SHIFT;
    const cy = w.y[s] >> FX_SHIFT;
    for (const a of allies[o]) {
      const v = w.visibility[a];
      for (let i = 0; i < pts.length; i += 2) {
        const x = cx + pts[i];
        const y = cy + pts[i + 1];
        if (x < 0 || y < 0 || x >= size || y >= size) continue;
        v[y * size + x] = 2;
      }
    }
    // Rules v9: a finished Wonder stands in plain sight of everyone: its footprint and one tile
    // around it are visible to every player, wherever they are.
    if (w.rules >= 9 && w.cat[s] === Cat.Building && w.kind[s] === WONDER) {
      const fs = KINDS[WONDER].building!.size;
      const x0 = w.btx[s] - 1;
      const y0 = w.bty[s] - 1;
      for (let p = 0; p < np; p++) {
        const v = w.visibility[p];
        for (let y = y0; y < y0 + fs + 2; y++)
          for (let x = x0; x < x0 + fs + 2; x++) if (x >= 0 && y >= 0 && x < size && y < size) v[y * size + x] = 2;
      }
    }
  }
}

const WONDER = kindIndex('wonder');

/** Reveal entire map for a player (spectators, replays, "explored" setting). */
export function revealAll(w: World, player: number): void {
  w.visibility[player]?.fill(2);
}
